import React, { createContext, useContext, useState, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';

const GamesContext = createContext();

const loadGamesFromLocalStorage = () => {
  try {
    const cached = localStorage.getItem('tm_games_cache');
    if (cached) {
      const parsed = JSON.parse(cached);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    }
  } catch (e) {
    console.warn('Failed to load games from localStorage:', e);
  }
  return [];
};

const loadStudiosFromLocalStorage = () => {
  try {
    const cached = localStorage.getItem('tm_studios_cache');
    if (cached) {
      const parsed = JSON.parse(cached);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    }
  } catch (e) {
    console.warn('Failed to load studios from localStorage:', e);
  }
  return [];
};

export const GamesProvider = ({ children }) => {
  // Synchronous lazy initialization ensures cached data is available on the very first render tick
  const [games, setGames] = useState(loadGamesFromLocalStorage);
  const [studios, setStudios] = useState(loadStudiosFromLocalStorage);
  const [loading, setLoading] = useState(false);
  const [studiosLoading, setStudiosLoading] = useState(false);

  const gamesRef = useRef(games);
  gamesRef.current = games;

  const studiosRef = useRef(studios);
  studiosRef.current = studios;

  const inFlightGamesPromiseRef = useRef(null);
  const inFlightStudiosPromiseRef = useRef(null);

  const fetchGames = useCallback(async (force = false) => {
    // Deduplicate in-flight fetch
    if (inFlightGamesPromiseRef.current) {
      return inFlightGamesPromiseRef.current;
    }

    if (!force) {
      const timestamp = localStorage.getItem('tm_games_cache_timestamp');
      const currentGames = gamesRef.current.length > 0 ? gamesRef.current : loadGamesFromLocalStorage();

      if (currentGames.length > 0) {
        if (gamesRef.current.length === 0) {
          setGames(currentGames);
        }
        if (timestamp) {
          const age = Date.now() - parseInt(timestamp, 10);
          if (age < 3600000) { // 1 hour TTL
            console.log(`Games cache is fresh (${(age / 60000).toFixed(1)} mins old). Skipping network fetch.`);
            return currentGames;
          }
        }
      }
    }

    const fetchPromise = (async () => {
      setLoading(true);
      let allGames = [];
      let gamesCursor = null;

      try {
        do {
          const gamesUrl = gamesCursor
            ? `${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/games?limit=2000&cursor=${encodeURIComponent(gamesCursor)}`
            : `${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/games?limit=2000`;

          const gamesResponse = await axios.get(gamesUrl, {
            headers: { 'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY },
          });

          const data = gamesResponse.data;
          const gamesChunk = data.games || (Array.isArray(data) ? data : []);
          allGames = [...allGames, ...gamesChunk];
          gamesCursor = data.nextCursor;
        } while (gamesCursor);

        setGames(allGames);
        gamesRef.current = allGames;
        try {
          localStorage.setItem('tm_games_cache', JSON.stringify(allGames));
          localStorage.setItem('tm_games_cache_timestamp', Date.now().toString());
        } catch (storageError) {
          console.warn('Failed to write games cache to localStorage:', storageError);
        }
        return allGames;
      } catch (err) {
        console.error('Error fetching games in background:', err);
        return gamesRef.current;
      } finally {
        setLoading(false);
        inFlightGamesPromiseRef.current = null;
      }
    })();

    inFlightGamesPromiseRef.current = fetchPromise;
    return fetchPromise;
  }, []);

  const fetchStudios = useCallback(async (force = false) => {
    // Deduplicate in-flight fetch
    if (inFlightStudiosPromiseRef.current) {
      return inFlightStudiosPromiseRef.current;
    }

    if (!force) {
      const timestamp = localStorage.getItem('tm_studios_cache_timestamp');
      const currentStudios = studiosRef.current.length > 0 ? studiosRef.current : loadStudiosFromLocalStorage();

      if (currentStudios.length > 0) {
        if (studiosRef.current.length === 0) {
          setStudios(currentStudios);
        }
        if (timestamp) {
          const age = Date.now() - parseInt(timestamp, 10);
          if (age < 3600000) { // 1 hour TTL
            console.log(`Studios cache is fresh (${(age / 60000).toFixed(1)} mins old). Skipping network fetch.`);
            return currentStudios;
          }
        }
      }
    }

    const fetchPromise = (async () => {
      setStudiosLoading(true);
      let allStudios = [];
      let studiosCursor = null;

      try {
        do {
          const studiosUrl = studiosCursor
            ? `${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/studios?limit=1000&cursor=${encodeURIComponent(studiosCursor)}`
            : `${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/studios?limit=1000`;

          const response = await axios.get(studiosUrl, {
            headers: { 'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY },
          });

          const data = response.data;
          const chunk = data.studios || (Array.isArray(data) ? data : []);
          allStudios = [...allStudios, ...chunk];
          studiosCursor = data.nextCursor;
        } while (studiosCursor);

        setStudios(allStudios);
        studiosRef.current = allStudios;
        try {
          localStorage.setItem('tm_studios_cache', JSON.stringify(allStudios));
          localStorage.setItem('tm_studios_cache_timestamp', Date.now().toString());
        } catch (storageError) {
          console.warn('Failed to write studios cache to localStorage:', storageError);
        }
        return allStudios;
      } catch (err) {
        console.error('Error fetching studios in background:', err);
        return studiosRef.current;
      } finally {
        setStudiosLoading(false);
        inFlightStudiosPromiseRef.current = null;
      }
    })();

    inFlightStudiosPromiseRef.current = fetchPromise;
    return fetchPromise;
  }, []);

  const addGameToCache = useCallback((newGame) => {
    setGames((prevGames) => {
      const exists = prevGames.some((g) => g.GameID === newGame.GameID);
      const updatedGames = exists
        ? prevGames.map((g) => (g.GameID === newGame.GameID ? { ...g, ...newGame } : g))
        : [...prevGames, newGame];
      try {
        localStorage.setItem('tm_games_cache', JSON.stringify(updatedGames));
        localStorage.setItem('tm_games_cache_timestamp', Date.now().toString());
      } catch (storageError) {
        console.warn('Failed to surgically update local games cache', storageError);
      }
      return updatedGames;
    });
  }, []);

  const updateGameInCache = useCallback((updatedFields) => {
    if (!updatedFields || !updatedFields.GameID) return;
    setGames((prevGames) => {
      const updatedGames = prevGames.map((g) =>
        g.GameID === updatedFields.GameID ? { ...g, ...updatedFields } : g
      );
      try {
        localStorage.setItem('tm_games_cache', JSON.stringify(updatedGames));
      } catch (storageError) {
        console.warn('Failed to update local games cache', storageError);
      }
      return updatedGames;
    });
  }, []);

  const removeGameFromCache = useCallback((gameId) => {
    if (!gameId) return;
    setGames((prevGames) => {
      const updatedGames = prevGames.filter((g) => g.GameID !== gameId);
      try {
        localStorage.setItem('tm_games_cache', JSON.stringify(updatedGames));
      } catch (storageError) {
        console.warn('Failed to remove game from local cache', storageError);
      }
      return updatedGames;
    });
  }, []);

  const addStudioToCache = useCallback((newStudio) => {
    if (!newStudio || !newStudio.StudioID) return;
    setStudios((prevStudios) => {
      const exists = prevStudios.some((s) => s.StudioID === newStudio.StudioID);
      const updatedStudios = exists
        ? prevStudios.map((s) => (s.StudioID === newStudio.StudioID ? { ...s, ...newStudio } : s))
        : [...prevStudios, newStudio];
      try {
        localStorage.setItem('tm_studios_cache', JSON.stringify(updatedStudios));
        localStorage.setItem('tm_studios_cache_timestamp', Date.now().toString());
      } catch (storageError) {
        console.warn('Failed to update local studios cache', storageError);
      }
      return updatedStudios;
    });
  }, []);

  const updateStudioInCache = useCallback((updatedFields) => {
    if (!updatedFields || !updatedFields.StudioID) return;
    setStudios((prevStudios) => {
      const updatedStudios = prevStudios.map((s) =>
        s.StudioID === updatedFields.StudioID ? { ...s, ...updatedFields } : s
      );
      try {
        localStorage.setItem('tm_studios_cache', JSON.stringify(updatedStudios));
      } catch (storageError) {
        console.warn('Failed to update local studios cache', storageError);
      }
      return updatedStudios;
    });
  }, []);

  const removeStudioFromCache = useCallback((studioId) => {
    if (!studioId) return;
    setStudios((prevStudios) => {
      const updatedStudios = prevStudios.filter((s) => s.StudioID !== studioId);
      try {
        localStorage.setItem('tm_studios_cache', JSON.stringify(updatedStudios));
      } catch (storageError) {
        console.warn('Failed to remove studio from local cache', storageError);
      }
      return updatedStudios;
    });
  }, []);

  const gameMap = useMemo(() => {
    const map = {};
    games.forEach((g) => {
      map[g.GameID] = g.Name;
    });
    return map;
  }, [games]);

  const fullGamesMap = useMemo(() => {
    const map = {};
    games.forEach((g) => {
      map[g.GameID] = g;
    });
    return map;
  }, [games]);

  const studioMap = useMemo(() => {
    const map = {};
    studios.forEach((s) => {
      map[s.StudioID] = s.Name;
    });
    return map;
  }, [studios]);

  return (
    <GamesContext.Provider
      value={{
        games,
        setGames,
        gameMap,
        fullGamesMap,
        loading,
        fetchGames,
        addGameToCache,
        updateGameInCache,
        removeGameFromCache,
        studios,
        setStudios,
        studioMap,
        studiosLoading,
        fetchStudios,
        addStudioToCache,
        updateStudioInCache,
        removeStudioFromCache,
      }}
    >
      {children}
    </GamesContext.Provider>
  );
};

export const useGamesCache = () => {
  return useContext(GamesContext);
};
